import React, { useState } from 'react';
import {
  User,
  Wrench,
  FileText,
  Camera,
  MapPin,
  Cpu,
  Bell,
  CheckCircle2,
  Scale,
  MessageSquare,
  Star,
  ShieldCheck,
  CreditCard,
  Send,
  ArrowRight,
  HelpCircle,
  AlertCircle,
  Building,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';

export const HowItWorksPage: React.FC = () => {
  const { navigateTo } = useApp();
  const [activeTab, setActiveTab] = useState<'customer' | 'professional'>('customer');

  const customerSteps = [
    {
      num: '01',
      title: 'Create an Account',
      desc: 'Sign up in seconds with your name, phone number, and Nigerian location to manage repair requests safely.',
      icon: User,
    },
    {
      num: '02',
      title: 'Describe the Job',
      desc: 'Tell us what needs fixing or installation — whether a leaking water tank, tripping circuit breaker, or custom carpentry.',
      icon: FileText,
    },
    {
      num: '03',
      title: 'Add Photos or Video',
      desc: 'Upload clear photos or short video clips of the faulty equipment or workspace so professionals can assess the scope accurately.',
      icon: Camera,
    },
    {
      num: '04',
      title: 'Select Location (State & LGA)',
      desc: 'Pinpoint your exact state and local government area in Nigeria so only nearby, active professionals respond.',
      icon: MapPin,
    },
    {
      num: '05',
      title: 'Automated Job Analysis',
      desc: 'JobMatch NG categorizes the work, estimates trade complexity, and highlights relevant safety advisories.',
      icon: Cpu,
    },
    {
      num: '06',
      title: 'Local Professionals Notified',
      desc: 'Verified professionals specializing in that trade within your coverage area receive instant job match alerts.',
      icon: Bell,
    },
    {
      num: '07',
      title: 'Receive Itemized Quotes',
      desc: 'Professionals review your job requirements and submit transparent proposals with labor and estimated material breakdowns in Naira.',
      icon: CreditCard,
    },
    {
      num: '08',
      title: 'Compare Quotes & Credentials',
      desc: 'Review pricing, professional verification badges, years of experience, and previous ratings side-by-side.',
      icon: Scale,
    },
    {
      num: '09',
      title: 'Select Your Professional',
      desc: 'Choose the best professional for your schedule and budget. Contact details are unlocked immediately upon selection.',
      icon: CheckCircle2,
    },
    {
      num: '10',
      title: 'Communicate & Arrange Work',
      desc: 'Chat or call to confirm timing, site access, material purchases, and execution details.',
      icon: MessageSquare,
    },
    {
      num: '11',
      title: 'Complete Job & Leave Review',
      desc: 'Inspect the completed work, confirm satisfaction, and leave a genuine rating to help other Nigerians find good tradespeople.',
      icon: Star,
    },
  ];

  const professionalSteps = [
    {
      num: '01',
      title: 'Create a Professional Account',
      desc: 'Register with your personal details, business name, and technical trade background.',
      icon: Wrench,
    },
    {
      num: '02',
      title: 'Select Trades & Service Areas',
      desc: 'Specify your primary trade (e.g., Plumbing, Electrical, AC) and the Nigerian LGAs you actively service without transit delays.',
      icon: MapPin,
    },
    {
      num: '03',
      title: 'Complete 4-Step Verification',
      desc: 'Submit your government NIN, address proof, service area confirmation, and complete a real-time biometric liveness check.',
      icon: ShieldCheck,
    },
    {
      num: '04',
      title: 'Free Marketplace Quoting Access',
      desc: 'Enjoy ₦0 monthly subscription fees. Once verified, you immediately receive local job matches and quote proposals with 90% direct earnings.',
      icon: CreditCard,
    },
    {
      num: '05',
      title: 'Receive Matching Job Opportunities',
      desc: 'Get immediate notifications whenever a homeowner or business posts a job matching your trade and location.',
      icon: Bell,
    },
    {
      num: '06',
      title: 'Review Job Details & Media',
      desc: 'Examine the customer’s problem description, photos, urgency level, and target budget before deciding to bid.',
      icon: FileText,
    },
    {
      num: '07',
      title: 'Submit a Clear Quote',
      desc: 'Provide an itemized bid detailing labor fees, material estimates, and expected turnaround time.',
      icon: Send,
    },
    {
      num: '08',
      title: 'Customer Selects Your Quote',
      desc: 'When the customer accepts your proposal, direct phone and in-app chat are enabled immediately.',
      icon: CheckCircle2,
    },
    {
      num: '09',
      title: 'Perform Work & Safety Standards',
      desc: 'Visit the site, carry out the work with technical diligence, and adhere to sound trade practices.',
      icon: Building,
    },
    {
      num: '10',
      title: 'Earn Verified Reviews',
      desc: 'Delighted customers leave public ratings on your profile, boosting your credibility and matching priority for future jobs.',
      icon: Star,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8 space-y-12">
      {/* Hero Header */}
      <div className="max-w-5xl mx-auto text-center space-y-4 pt-4 sm:pt-8">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold border border-emerald-200">
          <HelpCircle className="w-4 h-4 text-emerald-600" />
          <span>Transparent Nigerian Marketplace</span>
        </span>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
          How JobMatch NG Works
        </h1>
        <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
          Connecting verified skilled professionals with home and facility owners across Nigeria.
          Explore the exact step-by-step process below.
        </p>

        {/* Dual Mode Switcher */}
        <div className="inline-flex p-1.5 rounded-2xl bg-slate-200 border border-slate-300 shadow-inner mt-4">
          <button
            onClick={() => setActiveTab('customer')}
            className={`px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === 'customer'
                ? 'bg-emerald-600 text-white shadow-md'
                : 'text-slate-700 hover:text-slate-900'
            }`}
          >
            <User className="w-4 h-4" />
            <span>For Customers</span>
          </button>
          <button
            onClick={() => setActiveTab('professional')}
            className={`px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === 'professional'
                ? 'bg-emerald-600 text-white shadow-md'
                : 'text-slate-700 hover:text-slate-900'
            }`}
          >
            <Wrench className="w-4 h-4" />
            <span>For Professionals</span>
          </button>
        </div>
      </div>

      {/* Steps Grid */}
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(activeTab === 'customer' ? customerSteps : professionalSteps).map((step, idx) => {
            const Icon = step.icon;
            return (
              <div
                key={step.num}
                className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs hover:shadow-md transition-shadow relative flex flex-col justify-between space-y-4"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200">
                      Step {step.num}
                    </span>
                    <div className="w-10 h-10 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-700">
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>

                  <h3 className="text-base font-extrabold text-slate-900">
                    {step.title}
                  </h3>

                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    {step.desc}
                  </p>
                </div>

                <div className="pt-2 border-t border-slate-100 text-[11px] text-slate-400 font-medium">
                  {activeTab === 'customer' ? 'Customer Action' : 'Professional Action'}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Clarity / Responsibility Matrix */}
      <div className="max-w-4xl mx-auto bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">
            Platform vs. User Responsibilities
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 max-w-xl mx-auto">
            To maintain trust and clear expectations, here is what JobMatch NG handles automatically
            versus what customers and professionals are responsible for:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
          {/* Platform Role */}
          <div className="bg-emerald-50/60 rounded-2xl p-5 border border-emerald-200 space-y-3">
            <h4 className="font-bold text-sm text-emerald-950 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>What JobMatch NG Does</span>
            </h4>
            <ul className="space-y-2 text-xs text-emerald-900 leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="text-emerald-600 font-bold">•</span>
                <span>Validates professional government identification & biometric liveness data.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-600 font-bold">•</span>
                <span>Analyzes job requirements and classifies trades automatically.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-600 font-bold">•</span>
                <span>Notifies relevant local professionals within the customer's specified Nigerian LGA.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-600 font-bold">•</span>
                <span>Provides structured tools for quote comparison and in-app messaging.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-600 font-bold">•</span>
                <span>Maintains an independent Trust Desk to review reports and policy violations.</span>
              </li>
            </ul>
          </div>

          {/* User Role */}
          <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 space-y-3">
            <h4 className="font-bold text-sm text-slate-900 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-slate-600" />
              <span>What Users Are Responsible For</span>
            </h4>
            <ul className="space-y-2 text-xs text-slate-700 leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="text-slate-500 font-bold">•</span>
                <span>Providing accurate job descriptions and safe physical site access.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-500 font-bold">•</span>
                <span>Reviewing and mutually agreeing upon quote terms, materials, and schedules.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-500 font-bold">•</span>
                <span>Performing quality trade workmanship with appropriate technical diligence.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-500 font-bold">•</span>
                <span>Direct settlement of agreed service payments in Naira.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-500 font-bold">•</span>
                <span>Inspecting completed work prior to final sign-off and review submission.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Action CTA */}
      <div className="max-w-4xl mx-auto bg-slate-900 text-white rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-xl relative overflow-hidden">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
          Ready to Get Started with JobMatch NG?
        </h2>
        <p className="text-xs sm:text-sm text-slate-300 max-w-xl mx-auto leading-relaxed">
          Whether you need a trusted tradesperson for urgent repairs or want to grow your customer
          clientele as a certified professional, we are here to assist.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => navigateTo('post_job')}
            className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs sm:text-sm font-bold shadow-md transition-all flex items-center justify-center gap-2 min-h-[44px] cursor-pointer"
          >
            <span>Post a Job Free</span>
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigateTo('pro_auth', { authMode: 'register' })}
            className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs sm:text-sm font-semibold transition-all flex items-center justify-center gap-2 min-h-[44px] cursor-pointer"
          >
            <span>Register as a Professional</span>
          </button>
        </div>
      </div>
    </div>
  );
};
